/**
 * Media Asset models for the /api/media/* endpoints
 *
 * @remarks
 * Covers video (Cloudflare Stream via TUS) and audio (GCS via signed PUT).
 * Completely separate from UserMedia (Firebase Storage images) and
 * UserFile (Firebase Storage documents).
 *
 * @see MEDIA_ANGULAR_INTEGRATION.md for full API reference
 */

// ─── Enums ────────────────────────────────────────────────────────────────────

export type MediaAssetType = 'video' | 'audio';
export type MediaProcessingStatus = 'pending' | 'processing' | 'ready' | 'failed';

// ─── Sub-types ────────────────────────────────────────────────────────────────

export interface MediaCreator {
  _id: string;
  name: string;
  email: string;
  photoURL?: string;
}

export interface VideoPlaybackInfo {
  mediaType: 'video';
  cloudflareStreamId?: string;
  /** HLS manifest URL — token already appended by server if private */
  playbackUrl?: string;
  /** Cloudflare iframe embed URL */
  embedUrl?: string;
  thumbnailUrl?: string;
  durationSeconds?: number;
}

export interface AudioPlaybackInfo {
  mediaType: 'audio';
  /** GCS signed URL (private) or public URL */
  processedAudioUrl?: string;
  /** 0–500 normalised amplitude values in [0.0, 1.0] for waveform rendering */
  waveformData?: number[];
  durationSeconds?: number;
}

export type MediaAssetPlaybackInfo = VideoPlaybackInfo | AudioPlaybackInfo;

// ─── Core model ───────────────────────────────────────────────────────────────

export interface MediaAsset {
  _id: string;
  orgId: string;
  createdBy: MediaCreator;
  mediaType: MediaAssetType;
  fileName: string;
  title?: string;
  description?: string;
  mimeType: string;
  processingStatus: MediaProcessingStatus;
  processingError?: string;
  isPrivate: boolean;

  // Video-only (undefined when mediaType === 'audio')
  cloudflareStreamId?: string;
  cloudflarePlaybackUrl?: string;
  cloudflareThumbnailUrl?: string;
  videoDurationSeconds?: number;

  // Audio-only (undefined when mediaType === 'video')
  processedAudioUrl?: string;
  audioFileSizeBytes?: number;
  waveformData?: number[];
  audioDurationSeconds?: number;

  isDeleted: boolean;
  createdAt: string; // ISO 8601
  updatedAt: string;

  /** Injected by the API on single-asset and list endpoints */
  playbackInfo?: MediaAssetPlaybackInfo;
}

// ─── Pagination ───────────────────────────────────────────────────────────────

export interface MediaAssetPagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

// ─── Storage usage ────────────────────────────────────────────────────────────

export interface OrgStorageUsage {
  orgId: string;
  videoMinutes: number;
  videoCount: number;
  audioBytes: number;
  audioCount: number;
  imageBytes: number;
  imageCount: number;
  documentBytes: number;
  documentCount: number;
  lastRecalculatedAt?: string;
  limits: {
    videoMinutes: number;
    bytes: number;
  };
  computed: {
    videoMinutesUsedPct: string; // e.g. "42.5"
    totalBytesUsed: number;
  };
}

// ─── Dashboard sub-types ──────────────────────────────────────────────────────

export interface DailyTrendPoint {
  _id: string; // 'YYYY-MM-DD' UTC
  total: number;
  video: number;
  audio: number;
  ready: number;
  failed: number;
}

export interface RecentMediaFailure {
  _id: string;
  mediaType: MediaAssetType;
  fileName: string;
  processingError?: string;
  updatedAt: string;
}

export interface RecentReadyItem {
  _id: string;
  mediaType: MediaAssetType;
  fileName: string;
  title?: string;
  isPrivate: boolean;
  videoDurationSeconds?: number;
  audioDurationSeconds?: number;
  updatedAt: string;
}

export interface MediaDashboard {
  generatedAt: string; // ISO 8601 — use to show cache age

  storage: {
    video: {
      usedMinutes: number;
      limitMinutes: number;
      usedPercent: number | null;
      count: number;
    };
    other: {
      usedBytes: number;
      limitBytes: number;
      usedPercent: number | null;
      breakdown: {
        audio:    { bytes: number; count: number };
        image:    { bytes: number; count: number };
        document: { bytes: number; count: number };
      };
    };
    lastRecalculatedAt: string | null;
  };

  assets: {
    total: number;
    byStatus: {
      pending: number;
      processing: number;
      ready: number;
      failed: number;
    };
    byType: { video: number; audio: number };
    ready: {
      total: number;
      public: number;
      private: number;
      publishedPercent: number;
    };
  };

  video: {
    readyCount: number;
    totalMinutes: number;
    avgDurationSeconds: number | null;
    maxDurationSeconds: number | null;
  };

  audio: {
    readyCount: number;
    totalBytes: number;
    avgDurationSeconds: number | null;
    maxDurationSeconds: number | null;
  };

  activity: {
    uploads7d: number;
    uploads30d: number;
    readyIn30d: number;
    failureRate: number;
    dailyTrend: DailyTrendPoint[];
  };

  health: {
    stuckPending: number;
    totalFailed: number;
    recentFailures: RecentMediaFailure[];
  };

  recentReady: RecentReadyItem[];
}

// ─── Upload URL request bodies ────────────────────────────────────────────────

export interface GenerateVideoUploadUrlRequest {
  mediaType: 'video';
  orgId?: string;
  fileName: string;
  title?: string;
  description?: string;
  tags?: string[];
  isPrivate?: boolean;
  /** Exact file size in bytes. Required for quota pre-check. */
  fileSizeBytes: number;
  /** Max 43200 (12 hours). Used for quota pre-check. */
  maxDurationSeconds: number;
}

export interface GenerateAudioUploadUrlRequest {
  mediaType: 'audio';
  orgId?: string;
  fileName: string;
  mimeType: string; // Must be audio/* e.g. 'audio/mpeg', 'audio/wav'
  fileSizeBytes: number;
  title?: string;
  description?: string;
  tags?: string[];
  isPrivate?: boolean;
}

// ─── Upload URL responses ─────────────────────────────────────────────────────

export interface VideoUploadUrlResponse {
  success: true;
  data: {
    assetId: string;
    uploadUrl: string;
    uploadProtocol: 'tus';
    expiresAt: string;
  };
}

export interface AudioUploadUrlResponse {
  success: true;
  data: {
    assetId: string;
    uploadUrl: string;
    storagePath: string;
    uploadProtocol: 'https-put';
    expiresAt: string;
  };
}

// ─── Query params ─────────────────────────────────────────────────────────────

export interface ListMediaAssetsParams {
  orgId?: string;
  mediaType?: MediaAssetType;
  processingStatus?: MediaProcessingStatus;
  page?: number;
  limit?: number; // max: 50
}

export interface MediaLibraryParams {
  orgId?: string;
  type?: MediaAssetType;
  search?: string;
  processingStatus?: MediaProcessingStatus;
  page?: number;
  limit?: number; // max: 50
}

export interface GetMediaAssetParams {
  orgId?: string;
}

export interface MediaDashboardParams {
  orgId?: string;
}

// ─── API response wrappers ────────────────────────────────────────────────────

export interface ListMediaAssetsResponse {
  success: true;
  data: MediaAsset[];
  pagination: MediaAssetPagination;
}

export interface MediaLibraryResponse {
  success: true;
  data: MediaAsset[];
  pagination: MediaAssetPagination;
}

export interface GetMediaAssetResponse {
  success: true;
  data: MediaAsset;
}

export interface DeleteMediaAssetResponse {
  success: true;
  message: string;
}

export interface StorageUsageResponse {
  success: true;
  data: OrgStorageUsage;
}

export interface MediaDashboardResponse {
  success: true;
  data: MediaDashboard;
}

// ─── API error ────────────────────────────────────────────────────────────────

export interface MediaApiError {
  success: false;
  error: string;
  statusCode?: number;
}
